import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import MemberLayout from '../components/MemberLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';

export default function Quiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get(`/academy/quizzes/${quizId}`).then(r => setQuiz(r.data)).catch(e => { toast.error(e.response?.data?.detail || 'Gagal'); navigate('/academy'); }); }, [quizId]);

  const submit = async () => {
    if (Object.keys(answers).length < quiz.questions.length) return toast.error('Jawab semua pertanyaan');
    setSubmitting(true);
    try { const { data } = await api.post(`/academy/quizzes/${quizId}/submit`, { answers }); setResult(data); toast.success(`Skor: ${data.score}%`); }
    catch (e) { toast.error('Gagal'); } finally { setSubmitting(false); }
  };

  if (!quiz) return <MemberLayout><div className="h-64 flex items-center justify-center text-muted-foreground">Memuat quiz...</div></MemberLayout>;
  const resMap = result ? Object.fromEntries(result.results.map(r => [r.question_id, r])) : {};

  return (
    <MemberLayout>
      <Button variant="ghost" size="sm" onClick={() => navigate('/academy')} className="mb-4"><ArrowLeft className="h-4 w-4 mr-1" />Academy</Button>
      <h1 className="font-display text-2xl font-bold">{quiz.title}</h1>

      {result && (
        <Card className="p-6 mt-4 text-center bg-[hsl(var(--brand-gold)/0.08)]">
          <div className="font-display text-4xl font-bold">{result.score}%</div>
          <div className="text-sm text-muted-foreground mt-1">{result.correct} dari {result.total} benar</div>
        </Card>
      )}

      <div className="mt-6 space-y-4">
        {quiz.questions.map((q, qi) => {
          const r = resMap[q.question_id];
          return (
            <Card key={q.question_id} className="p-5">
              <div className="font-medium">{qi+1}. {q.question}</div>
              <div className="mt-3 space-y-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[q.question_id] === oi;
                  let cls = 'border-border hover:bg-muted';
                  if (r) {
                    if (oi === r.correct_answer) cls = 'border-emerald-400 bg-emerald-50';
                    else if (oi === r.your_answer) cls = 'border-red-400 bg-red-50';
                    else cls = 'border-border opacity-70';
                  } else if (selected) cls = 'border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold)/0.1)]';
                  return (
                    <button key={oi} disabled={!!result} onClick={() => setAnswers({ ...answers, [q.question_id]: oi })}
                      data-testid={`quiz-option-${qi}-${oi}`}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${cls}`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
              {r && (
                <div className={`mt-3 flex items-start gap-2 text-sm rounded-lg p-3 ${r.correct ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                  {r.correct ? <CheckCircle2 className="h-4 w-4 mt-0.5" /> : <XCircle className="h-4 w-4 mt-0.5" />}
                  <div><div className="font-medium">{r.correct ? 'Benar' : 'Kurang tepat'}</div><div>{r.explanation}</div><div className="text-xs mt-1 opacity-80">Lesson terkait: {r.related_lesson}</div></div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {!result ? (
        <Button onClick={submit} disabled={submitting} className="w-full mt-6 h-11 bg-[hsl(var(--brand-ink))] text-white" data-testid="quiz-submit-button">{submitting ? 'Mengirim...' : 'Kirim Jawaban'}</Button>
      ) : (
        <div className="flex gap-3 mt-6"><Button variant="outline" className="flex-1" onClick={() => { setResult(null); setAnswers({}); }}>Ulangi</Button><Button className="flex-1" onClick={() => navigate('/academy')}>Kembali ke Academy</Button></div>
      )}
    </MemberLayout>
  );
}
